import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import PaymentLayout from 'shared/lib/components/PaymentLayout';
import ConfirmationLayout from 'shared/lib/components/ConfirmationLayout';
import LineItemsList from 'shared/lib/components/LineItemsList';
import SectionWrapper from 'shared/lib/components/SectionWrapper';
import CardSectionHeading from '../CardSectionHeading';
import CardRowKeyVal from '../CardRowKeyVal';
import CardRowLink from '../CardRowLink';
import moment from 'moment-timezone';
import get from 'lodash/get';
import map from 'lodash/map';
import currencyFormatter from 'currency-formatter';
import { Modal } from 'sure-components';
import Button from 'shared/lib/components/Button';
import './PolicyDetailsCollectibles.css';

const propTypes = {
  details: PropTypes.shape({
    collections: PropTypes.array,
    deductible: PropTypes.number,
    owners: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
      })
    ),
    ownership_type: PropTypes.string,
    ownership_type_name: PropTypes.string,
  }),
};

const defaultProps = {
  details: {},
};

class PolicyDetailsCollectibles extends PureComponent {
  constructor(props) {
    super(props);
    this.MODALS = {
      EDIT: 'EDIT',
      REMOVE_CHECKOUT: 'REMOVE_CHECKOUT',
      REMOVE_CONFIRMATION: 'REMOVE_CONFIRMATION',
    };
    this.state = {
      activeModal: null,
      removeCheckoutInfo: {},
      editingCollection: null,
    };
  }

  handleRemoveCollection = () => {
    const collection = this.state.editingCollection;
    this.props
      .getEndorsementRemoveCheckout({
        categoryCode: collection.category.code,
        policyId: this.props.id,
      })
      .then(removeCheckoutInfo => {
        this.setState({
          activeModal: this.MODALS.REMOVE_CHECKOUT,
          removeCheckoutInfo,
        });
      });
  };

  handleRemoveCollectionSubmit = () => {
    const collection = this.state.editingCollection;
    this.props
      .submitEndorsementRemove({
        categoryCode: collection.category.code,
        policyId: this.props.id,
      })
      .then(() => {
        this.setState({
          activeModal: this.MODALS.REMOVE_CONFIRMATION,
        });
      })
      .catch(this.props.onError);
  };

  showEditModal = collection => {
    this.setState({
      activeModal: this.MODALS.EDIT,
      editingCollection: collection,
    });
  };

  hideModals = () => {
    this.setState({
      activeModal: null,
      editingCollection: null,
      removeCheckoutInfo: {},
    });
  };

  handleEditCollection = () => {
    const collection = this.state.editingCollection;
    this.props.updateCollectiblesEndorsementAddress({
      streetAddress: collection.address.line1,
      unit: collection.address.line2,
      city: collection.address.city,
      region: collection.address.region,
      postal: collection.address.postal,
      country: collection.address.country,
    });

    this.hideModals();
    this.props.navTo(
      `/account/policies/collectibles/${this.props.id}/edit/${
        collection.category.code
      }`
    );
  };

  get coverageStartDate() {
    return moment(this.props.coverage_start_date, 'YYYY-MM-DD').format(
      'MMM DD, YYYY'
    );
  }

  get coverageEndDate() {
    return moment(this.props.coverage_end_date, 'YYYY-MM-DD').format(
      'MMM DD, YYYY'
    );
  }

  get deductiblePriceStr() {
    return currencyFormatter.format(get(this.props.details, 'deductible'), {
      code: get(this.props.billing, 'currency_code'),
    });
  }

  get ownersNode() {
    const { ownership_type, ownership_entity, owners } = this.props.details;

    switch (ownership_type) {
      case 'personal':
        return <CardRowKeyVal title="Owner" detail={ownership_entity} />;
      case 'foundation':
        return <CardRowKeyVal title="Foundation" detail={ownership_entity} />;
      case 'musical_group':
        return (
          <CardRowKeyVal title="Musical group" detail={ownership_entity} />
        );
      case 'trust':
        return [
          <CardRowKeyVal key="trust" title="Trust" detail={ownership_entity} />,
          ...map(owners, (owner, i) => (
            <CardRowKeyVal key={i} title="Trustee" detail={owner.name} />
          )),
        ];
      case 'jointly_owned':
        return map(owners, (owner, i) => (
          <CardRowKeyVal key={i} title="Joint owner" detail={owner.name} />
        ));
      case 'executor':
        return map(owners, (owner, i) => (
          <CardRowKeyVal
            key={i}
            title="Executor/Administrator"
            detail={owner.name}
          />
        ));
      default:
        return null;
    }
  }

  get collectionDetailsNode() {
    return map(get(this.props.details, 'collections'), (collection, i) => {
      const highestValPriceStr = currencyFormatter.format(
        collection.highest_value_item,
        {
          code: this.props.billing.currency_code,
        }
      );
      const totalValPriceStr = currencyFormatter.format(
        collection.collection_total_value,
        {
          code: this.props.billing.currency_code,
        }
      );
      return (
        <div key={i}>
          <CardSectionHeading
            actionProps={{
              'data-testid': 'editCollectionBtn',
            }}
            actionTitle={
              this.props.status === 'approved' &&
              collection.category.code !== 'jewelry'
                ? 'Edit'
                : null
            }
            onAction={() => this.showEditModal(collection)}
          >
            {collection.category.name} Collection Details
          </CardSectionHeading>
          <CardRowKeyVal
            title="Collection type"
            detail={collection.category.name}
          />
          <CardRowKeyVal
            title="Purchased on"
            detail={moment(collection.created).format('MMM DD, YYYY')}
          />
          <CardRowKeyVal
            title="Highest valued item"
            detail={highestValPriceStr}
          />
          <CardRowKeyVal title="Total value" detail={totalValPriceStr} />
          <CardRowKeyVal
            title="Location of collection"
            detail={
              <div>
                {collection.address.line1}
                {collection.address.line2 && (
                  <span>
                    {collection.address.line2}
                    <br />
                  </span>
                )}
                <br />
                {collection.address.city}, {collection.address.region}{' '}
                {collection.address.postal}
              </div>
            }
          />
        </div>
      );
    });
  }

  render() {
    const { details, documents, getPolicyById, id } = this.props;
    const { activeModal, editingCollection, removeCheckoutInfo } = this.state;

    return (
      <Fragment>
        <CardRowKeyVal
          title="Coverage period"
          detail={`From ${this.coverageStartDate} to ${this.coverageEndDate}`}
        />
        {this.ownersNode}
        <CardSectionHeading>Policy Selections</CardSectionHeading>
        <CardRowKeyVal title="Deductible" detail={this.deductiblePriceStr} />

        {this.collectionDetailsNode}

        <Modal
          className="ModalCollectiblesEditCollection"
          title={`Edit ${get(editingCollection, 'category.name')} collection`}
          isActive={activeModal === this.MODALS.EDIT}
          onDismiss={this.hideModals}
        >
          <CardRowLink onClick={this.handleEditCollection}>
            Edit collection
          </CardRowLink>
          {details.collections && details.collections.length > 1 && (
            <CardRowLink
              type="destructive"
              onClick={this.handleRemoveCollection}
            >
              Remove collection
            </CardRowLink>
          )}
        </Modal>

        <Modal
          className="ModalCollectiblesRemoveCollection"
          title="Remove collection"
          isActive={activeModal === this.MODALS.REMOVE_CHECKOUT}
          onDismiss={this.hideModals}
        >
          <PaymentLayout
            title="Your Reduced Quote"
            notice={`YOUR COLLECTION WILL BE REMOVED FROM YOUR EXISTING POLICY.`}
            policyDocUrl={get(documents, '[0].url')}
            icon="widgets"
            billingCycle="year"
            isPurchaseInProgress={false}
            pricePerCycle={get(removeCheckoutInfo, 'new_annual_total_premium')}
            totals={get(removeCheckoutInfo, 'cart.totals')}
            disclaimer=""
            hideBillingSummary
            refundDetails={removeCheckoutInfo.refund_details}
            action={get(removeCheckoutInfo, 'action')}
            onSubmit={this.handleRemoveCollectionSubmit}
          >
            <SectionWrapper title="Coverage details">
              <LineItemsList
                rows={[
                  [
                    'Previous premium',
                    `${currencyFormatter.format(
                      get(removeCheckoutInfo, 'previous_annual_total_premium'),
                      { code: 'USD' }
                    )}/year`,
                  ],
                  ['Underwritten by', 'Markel'],
                  [
                    'Deductible',
                    currencyFormatter.format(get(details, 'deductible'), {
                      code: 'USD',
                    }),
                  ],
                  [
                    'Coverage term',
                    `${moment().format('MMM DD, YYYY')} - ${
                      this.coverageEndDate
                    }`,
                  ],
                ]}
              />
            </SectionWrapper>
          </PaymentLayout>
        </Modal>

        <Modal
          className="ModalCollectiblesRemoveCollectionConfirmation"
          title="Remove collection"
          isActive={activeModal === this.MODALS.REMOVE_CONFIRMATION}
          onDismiss={() => getPolicyById(id) && this.hideModals()}
        >
          <ConfirmationLayout
            message="Coverage will not bind until the insurance carrier approves your application. You will receive a confirmation email updating your application status within the next business day."
            policyDocUrl={get(documents, '[0].url')}
            icon="widgets"
            totals={get(removeCheckoutInfo, 'cart.totals')}
            pricePerCycle={get(removeCheckoutInfo, 'new_annual_total_premium')}
            isPendingApproval={true}
            hideSmsForm
          >
            <SectionWrapper title="Coverage details">
              <LineItemsList
                rows={[
                  [
                    'Previous premium',
                    `${currencyFormatter.format(
                      get(removeCheckoutInfo, 'previous_annual_total_premium'),
                      { code: 'USD' }
                    )}/year`,
                  ],
                  ['Underwritten by', 'Markel'],
                  [
                    'Deductible',
                    currencyFormatter.format(get(details, 'deductible'), {
                      code: 'USD',
                    }),
                  ],
                  [
                    'Coverage term',
                    `${moment().format('MMM DD, YYYY')} - ${
                      this.coverageEndDate
                    }`,
                  ],
                ]}
              />
            </SectionWrapper>

            <SectionWrapper title="Billing details">
              <LineItemsList
                rows={[
                  [
                    <strong>Refund amount</strong>,
                    <strong>
                      {currencyFormatter.format(
                        get(
                          removeCheckoutInfo,
                          'refund_details.total_refund_amount'
                        ),
                        { code: 'USD' }
                      )}
                    </strong>,
                  ],
                ]}
              />
            </SectionWrapper>
          </ConfirmationLayout>
          <Button onClick={() => getPolicyById(id) && this.hideModals()}>
            Done
          </Button>
        </Modal>
      </Fragment>
    );
  }
}

PolicyDetailsCollectibles.propTypes = propTypes;
PolicyDetailsCollectibles.defaultProps = defaultProps;

export default PolicyDetailsCollectibles;
